import { useState } from 'react';

type Mode = 'login' | 'register';

interface AuthModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (email: string, password: string) => Promise<void>;
}

export default function AuthModal({
  onClose,
  onSuccess,
  onLogin,
  onRegister,
}: AuthModalProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await onLogin(email.trim(), password);
      } else {
        await onRegister(email.trim(), password);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Noget gik galt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-title"
    >
      <div className="surface w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 id="auth-title" className="text-xl font-semibold text-strong">
            {mode === 'login' ? 'Log ind' : 'Opret konto'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-faint hover:text-strong"
            aria-label="Luk"
          >
            ✕
          </button>
        </div>

        <p className="mt-2 text-sm text-muted">
          {mode === 'login'
            ? 'Gem din fremgang på tværs af enheder.'
            : 'Valgfrit — du kan også øve uden konto.'}
        </p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block">
            <span className="label">E-mail</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-input mt-1 w-full rounded-lg bg-white px-3 py-2 text-zinc-900 ring-1 ring-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-white/10"
            />
          </label>
          <label className="block">
            <span className="label">Adgangskode</span>
            <input
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input mt-1 w-full rounded-lg bg-white px-3 py-2 text-zinc-900 ring-1 ring-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-white/10"
            />
          </label>
          {error && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full disabled:opacity-50"
          >
            {busy ? 'Vent…' : mode === 'login' ? 'Log ind' : 'Opret konto'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-muted">
          {mode === 'login' ? (
            <>
              Ingen konto?{' '}
              <button
                type="button"
                className="text-indigo-600 hover:underline dark:text-indigo-400"
                onClick={() => setMode('register')}
              >
                Opret en
              </button>
            </>
          ) : (
            <>
              Har du allerede konto?{' '}
              <button
                type="button"
                className="text-indigo-600 hover:underline dark:text-indigo-400"
                onClick={() => setMode('login')}
              >
                Log ind
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
